import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {
  CategoryType,
  CompetitorResults,
  EventItem,
  FINISH_CONTROL_POINT_TYPE,
  FullScreenMode,
  Lookups,
  Race,
  RaceSettings,
  ResultProcessingType
} from "@model";
import {animate, query, stagger, style, transition, trigger} from "@angular/animations";
import {AbstractComponent} from "../abstract.component";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {ResultsDetailsModalComponent} from "../results-details/results-details-modal/results-details-modal.component";
import {finalize} from "rxjs";
import {CompetitorResultsService, LoaderService} from "@services";

@Component({
  selector: 'app-live-results-table-body',
  templateUrl: './live-results-table-body.component.html',
  styleUrls: ['./live-results-table-body.component.scss'],
  animations: [
    trigger('listEnter', [
      transition('* => *', [
        query(':enter .grid-cell', [
          style({ opacity: 0, transform: 'translateY(8px)' }),
          stagger(40, [
            animate('320ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
          ]),
        ], { optional: true }),
      ]),
    ]),
    trigger('rowUpdate', [
      transition('* => highlight', [animate('500ms ease-out')]),
      transition('* => move',      [animate('500ms ease-in-out')]),
      transition('* => idle',      []),
    ]),
  ],
  standalone: false
})
export class LiveResultsTableBodyComponent extends AbstractComponent implements OnInit, OnChanges, OnDestroy {

  static readonly PAGE_SIZE = 7;
  static readonly CAROUSEL_INTERVAL = 6000;
  static readonly DISPLAY_INTERVAL = 20000;
  static readonly BASE_COLUMN_COUNT = 4;


  @Input() competitorLiveResults: CompetitorResults[] = [];
  @Input() race: Race | undefined;
  @Input() event: EventItem | undefined = undefined;
  @Input({required: true}) languages: Lookups[] = [];
  @Input() isRoundRace?: boolean;
  @Input() measurementType: number | undefined = undefined;
  @Input() gender?: number;
  @Input() category?: number;
  @Input() raceSettings: RaceSettings | undefined;
  @Input() isFullScreen: boolean = false;
  @Input() isDarkMode: boolean = false;
  @Input() isFinished: boolean = false;
  @Input() isCarousel = false;
  @Input() fullScreenMode: FullScreenMode = FullScreenMode.FULL;

  // ── Change-detection baseline ─────────────────────────────────────────────
  private previousIds = new Set<number>();

  // ── Initialization guard ──────────────────────────────────────────────────
  // True after the very first non-empty result set has been processed.
  // Used to detect a mid-race join: if the first real batch already has more
  // than PAGE_SIZE+3 rows we skip the staggered reveal and jump to the end.
  private initialized = false;

  private leadingStage: number | null = null;
  private displayedIds = new Set<number>();

  private undisplayedQueue: LiveResultTableRowData[] = [];
  private displayTimer: ReturnType<typeof setInterval> | null = null;

  // ── Template-visible state ────────────────────────────────────────────────
  firstCompetitorResults: CompetitorResults | undefined = undefined;
  tableData: LiveResultTableRowData[] = [];
  fixedRows: LiveResultTableRowData[] = [];
  pagedRows: LiveResultTableRowData[] = [];
  pages: LiveResultTableRowData[][] = [];

  showCategories = false;
  columnCount = LiveResultsTableBodyComponent.BASE_COLUMN_COUNT;
  carouselInterval = LiveResultsTableBodyComponent.CAROUSEL_INTERVAL;
  pauseOnHover = false;
  pauseOnFocus = false;
  wrap = true;
  listAnimState = 0;
  currentPageIndex = 0;
  showCategoryCol: boolean = false;
  showLapCol: boolean = false;
  showDistanceCol: boolean = false;
  showTeamCol: boolean = false;
  showGapCol: boolean = false;

  constructor(private modalService: NgbModal, private loaderService: LoaderService, private competitorResultsService: CompetitorResultsService) { super(); }


  ngOnInit(): void {
    this.showCategories =
        !!this.raceSettings?.category?.categoryType &&
        this.raceSettings.category.categoryType !== CategoryType.NOT_USED;

    if (this.isCarousel) {
      const seconds = this.raceSettings?.rotatingResultsSeconds
          ?? (LiveResultsTableBodyComponent.CAROUSEL_INTERVAL / 1000);
      this.carouselInterval = seconds * 1000;
    }

    if (this.competitorLiveResults?.length) {
      this.rebuildTableData(this.competitorLiveResults);
    }
    this.initCols();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const fm = changes['fullScreenMode']
    if (!fm) {
      this.initCols();
    }
    const cr = changes['competitorLiveResults'];
    if (!cr || !Array.isArray(cr.currentValue)) return;
    this.rebuildTableData(cr.currentValue, cr.previousValue ?? []);
  }


  initCols() {
    this.showCategoryCol = this.showCategories;
    this.showLapCol = this.isRoundRace ?? false;
    this.showDistanceCol = this.race?.raceSettings?.resultProcessingType === ResultProcessingType.TIME_LIMITED;
    this.showTeamCol = this.fullScreenMode !== FullScreenMode.SPLIT;
    this.showGapCol = !this.isFullScreen;
    this.columnCount = LiveResultsTableBodyComponent.BASE_COLUMN_COUNT
        + (this.showCategoryCol ? 1 : 0)
        + (this.showLapCol ? 1 : 0)
        + (this.showDistanceCol ? 1 : 0)
        + (this.showGapCol ? 1 : 0)
        + (this.showTeamCol ? 1 : 0);
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.clearTimer();
  }

  private rebuildTableData(current: CompetitorResults[], previous: CompetitorResults[] = []): void {
    const wasEmpty   = this.previousIds.size === 0;
    const currentIds = new Set<number>();
    const newTableData: LiveResultTableRowData[] = [];

    // If this is the very first real batch AND it already has more rows than one
    // full page (3 fixed + PAGE_SIZE paged), the component is joining a race
    // already in progress. Drain the entire queue instantly without animation.
    const isLateJoin = wasEmpty && current.length > (3 + LiveResultsTableBodyComponent.PAGE_SIZE);

    current.forEach((result, index) => {
      const id  = result.id!;
      currentIds.add(id);
      const wasPresent = this.previousIds.has(id);
      const prevIndex = wasPresent ? previous.findIndex(r => r.id === id) : -1;
      const delta = prevIndex >= 0 && prevIndex !== index ? (index - prevIndex) * 100 : 0;

      const displayed = this.displayedIds.has(id);

      newTableData.push(new LiveResultTableRowData(delta, false, id, result, !wasPresent && !displayed, displayed));
    });

    this.previousIds.forEach(id => { if (!currentIds.has(id)) this.previousIds.delete(id); });
    currentIds.forEach(id => this.previousIds.add(id));

    this.tableData              = newTableData;
    this.firstCompetitorResults = newTableData[0]?.competitorResults ?? undefined;

    this.rebuildPages(wasEmpty, isLateJoin);

    if (wasEmpty && newTableData.length > 0) this.listAnimState++;

    this.initialized = true;

    setTimeout(() => {
      this.tableData.forEach(r => { r.updated = false; r.delta = 0; r.isNew = false; });
    }, 550);
  }

  // ── Page building ─────────────────────────────────────────────────────────

  private rebuildPages(wasEmpty: boolean, isLateJoin = false): void {
    if (this.isCarousel) {
      this.fixedRows = this.tableData.slice(0, 3);
      this.pages = this.chunk(this.tableData.slice(3), LiveResultsTableBodyComponent.PAGE_SIZE);
      this.pagedRows = [];
      return;
    }

    const newStage     = this.resolveLeadingStage();
    const stageChanged = newStage !== null && newStage !== this.leadingStage;

    if (stageChanged) {
      this.leadingStage = newStage;
      // Stage advanced: wipe everything and let all competitors animate in fresh.
      this.pagedRows        = [];
      this.undisplayedQueue = [];
      this.displayedIds.clear();
      this.tableData.forEach(r => r.displayed = false);
      this.clearTimer();
    }

    const allStageRows = this.getAllStageRows();
    this.fixedRows     = allStageRows.slice(0, 3);

    const stageRows = allStageRows.slice(3);

    // Only enqueue competitors that haven't been displayed yet this stage.
    const queuedIds   = new Set(this.undisplayedQueue.map(r => r.id!));
    const newArrivals = stageRows.filter(
        r => r.id != null && !this.displayedIds.has(r.id!) && !queuedIds.has(r.id!)
    );
    this.undisplayedQueue.push(...newArrivals);

    // Refresh data on already-visible rows (times, gaps, positions).
    const byId = new Map(stageRows.map(r => [r.id, r]));
    this.pagedRows = this.pagedRows
        .map(r => byId.get(r.id!) ?? r)
        .filter(r => byId.has(r.id!));

    // Refresh queue objects in-place.
    this.undisplayedQueue = this.undisplayedQueue
        .map(r => byId.get(r.id!) ?? r)
        .filter(r => byId.has(r.id!));

    if (this.isFinished) {
      // Race over: freeze on top 10 — first 3 already in fixedRows, show next 7 instantly.
      this.showFinishedTop();
    } else if (isLateJoin) {
      // Mid-race join: skip the whole reveal queue and show the last page directly.
      this.skipToEnd();
    } else if (wasEmpty || stageChanged) {
      this.drainQueue();
    }

    if (!this.isFinished && this.undisplayedQueue.length > 0) {
      this.ensureTimer();
    } else {
      this.clearTimer();
    }
  }

  // ── Queue skip (late join) ────────────────────────────────────────────────

  /**
   * Drains the entire queue at once without triggering the staggered animation.
   * Shows only the last PAGE_SIZE rows (the most current results), marks
   * every competitor as displayed so they're never re-queued.
   */
  private skipToEnd(): void {
    const size = LiveResultsTableBodyComponent.PAGE_SIZE;

    // Mark everything as displayed — no re-queuing ever.
    this.undisplayedQueue.forEach(r => {
      r.displayed = true;
      if (r.id != null) this.displayedIds.add(r.id);
    });

    // Show only the trailing PAGE_SIZE entries (most recent finishers / leaders).
    const lastPage = this.undisplayedQueue.slice(-size);
    this.undisplayedQueue = [];

    this.pagedRows = lastPage;
    // No listAnimState bump — we deliberately skip the enter animation.

    this.clearTimer();
  }

  // ── Finished top-10 ──────────────────────────────────────────────────────

  /**
   * Race is finished: fixedRows already holds positions 1–3.
   * Show positions 4–10 in pagedRows instantly (no animation, no timer).
   */
  private showFinishedTop(): void {
    const size = LiveResultsTableBodyComponent.PAGE_SIZE;
    const top  = this.undisplayedQueue.slice(0, size);

    top.forEach(r => {
      r.displayed = true;
      if (r.id != null) this.displayedIds.add(r.id);
    });
    this.undisplayedQueue = [];

    this.pagedRows = top;
    // No listAnimState bump — appear instantly, no stagger.
  }

  private drainQueue(): void {
    if (this.undisplayedQueue.length === 0) {
      this.clearTimer();
      return;
    }

    const size  = LiveResultsTableBodyComponent.PAGE_SIZE;
    const count = Math.min(this.undisplayedQueue.length, size);
    const next  = this.undisplayedQueue.splice(0, count);

    next.forEach(r => {
      r.displayed = true;
      if (r.id != null) this.displayedIds.add(r.id);
    });

    this.pagedRows = [...this.pagedRows.slice(count), ...next];
    this.listAnimState++;

    if (this.undisplayedQueue.length === 0) {
      this.clearTimer();
    }
  }

  // ── Timer ─────────────────────────────────────────────────────────────────

  private ensureTimer(): void {
    if (this.displayTimer !== null) return;
    this.displayTimer = setInterval(() => this.drainQueue(), LiveResultsTableBodyComponent.DISPLAY_INTERVAL);
  }

  private clearTimer(): void {
    if (this.displayTimer !== null) {
      clearInterval(this.displayTimer);
      this.displayTimer = null;
    }
  }

  // ── Stage helpers ─────────────────────────────────────────────────────────

  /** All tableData rows that belong to the leading stage (no positional slice). */
  private getAllStageRows(): LiveResultTableRowData[] {
    if (this.leadingStage === null) return this.tableData;
    return this.tableData.filter(r => this.resolveRowStage(r) === this.leadingStage);
  }

  private resolveLeadingStage(): number | null {
    const detail = this.firstCompetitorResults?.competitorResultsDetail;
    if (!detail) return null;

    if (this.isRoundRace) {
      if (!detail.laps) return null;
      const keys = Object.keys(detail.laps).map(k => Number(k)).filter(n => !isNaN(n));
      if (keys.length === 0) return null;
      return Math.max(...keys);
    }

    const stage = detail.ltpCompetitorStageDetail?.tp === FINISH_CONTROL_POINT_TYPE
        ? -1 : detail.ltpCompetitorStageDetail?.ordinalNumber;
    return typeof stage === 'number' ? stage : null;
  }

  private resolveRowStage(row: LiveResultTableRowData): number | null {
    const detail = row.competitorResults?.competitorResultsDetail;
    if (!detail) return null;

    if (this.isRoundRace) {
      if (!detail.laps) return null;
      const keys = Object.keys(detail.laps).map(k => Number(k)).filter(n => !isNaN(n));
      if (keys.length === 0) return null;
      return Math.max(...keys);
    }

    const stage = detail.ltpCompetitorStageDetail?.tp === FINISH_CONTROL_POINT_TYPE
        ? -1 : detail.ltpCompetitorStageDetail?.ordinalNumber;
    return typeof stage === 'number' ? stage : null;
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  animState(row: LiveResultTableRowData): string {
    if (row.updated) return 'highlight';
    if (row.delta)   return 'move';
    return 'idle';
  }

  trackById = (_: number, row: LiveResultTableRowData) => row?.id ?? _;


  openDetailsModal(competitorResults: CompetitorResults | undefined): void {
    if (!competitorResults) return;
    const isConcurrent = RaceSettings.isConcurrentRace(this.race?.raceSettings);

    if (isConcurrent && competitorResults.id) {
      this.loaderService.startLoading();
      this.competitorResultsService.getDetailedCompetitorResults(competitorResults.id).pipe(
          finalize(() => this.loaderService.dismissLoading())
      ).subscribe(detailed => {
        this.openDetailsModalWithResults(detailed ?? competitorResults);
      });
    }
    else {
      this.openDetailsModalWithResults(competitorResults);
    }
  }

  openDetailsModalWithResults(competitorResults: CompetitorResults | undefined): void {
    if (!competitorResults) return;
    const modalRef = this.modalService.open(ResultsDetailsModalComponent, {
      centered:    true,
      size: 'xl',
      windowClass: 'results-details-modal',
    });
    modalRef.componentInstance.competitorResults = competitorResults;
    modalRef.componentInstance.measurementType   = this.measurementType;
    modalRef.componentInstance.category          = this.category;
    modalRef.componentInstance.gender            = this.gender;
    modalRef.componentInstance.isRoundRace       = this.isRoundRace;
    modalRef.componentInstance.showDistance      =
        this.race?.raceSettings?.resultProcessingType === ResultProcessingType.TIME_LIMITED;
    modalRef.componentInstance.race              = this.race;
    modalRef.componentInstance.event             = this.event;
    modalRef.componentInstance.languages         = this.languages;
    modalRef.componentInstance.isAdmin           = false;
  }

  private chunk<T>(arr: T[], size: number): T[][] {
    const out: T[][] = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
  }

  pageId(i: number) { return `page-${i}`; }

  protected readonly ResultProcessingType = ResultProcessingType;
  protected readonly FullScreenMode = FullScreenMode;
}

export class LiveResultTableRowData {
  constructor(
      public delta: number = 0,
      public updated: boolean = false,
      public id?: number,
      public competitorResults?: CompetitorResults,
      public isNew: boolean = false,
      public displayed: boolean = false,
  ) {}
}